import React, { useRef, useState } from 'react';
import axios from 'axios';
import { FiUpload, FiX } from 'react-icons/fi';
import { getAdsMediaDisplayUrl, getAdsVideoSrc } from '../utils/adsMediaUrl';

const API_BASE = process.env.REACT_APP_API_URL || '';

const MEDIA_TYPE_TO_FOLDER = {
    adsImage: 'image',
    adsGif: 'gif',
    adsVideos: 'video',
};

const MEDIA_TYPE_ACCEPT = {
    adsImage: 'image/jpeg,image/png,image/webp,image/*',
    adsGif: 'image/gif',
    adsVideos: 'video/*',
};

/**
 * Drag & drop + file picker for ads media (R2: ads/{image|gif|video}/).
 * adId optional — create flow uploads standalone; edit flow updates ad document.
 */
const AdsMediaUploadSection = ({
    label,
    mediaType,
    urls = [],
    onUrlsChange,
    adId = null,
    multiple = true,
    disabled = false,
}) => {
    const [uploading, setUploading] = useState(false);
    const [error, setError] = useState('');
    const [dragOver, setDragOver] = useState(false);
    const [videoDirectFallback, setVideoDirectFallback] = useState({});
    const inputRef = useRef(null);

    const folder = MEDIA_TYPE_TO_FOLDER[mediaType] || 'image';
    const accept = MEDIA_TYPE_ACCEPT[mediaType] || 'image/*';
    const isVideo = mediaType === 'adsVideos';
    const isGif = mediaType === 'adsGif';

    const handleFiles = async (files) => {
        if (!files || files.length === 0 || disabled) return;
        const list = Array.from(files);
        setError('');
        setUploading(true);

        try {
            const formData = new FormData();
            list.forEach((f) => formData.append('files', f));
            const token = localStorage.getItem('token');
            const uploadUrl = adId
                ? `${API_BASE}/api/ads/${adId}/media/${folder}`
                : `${API_BASE}/api/ads/media/${folder}`;

            const res = await axios.post(uploadUrl, formData, {
                headers: {
                    'Content-Type': 'multipart/form-data',
                    Authorization: `Bearer ${token}`,
                    'ngrok-skip-browser-warning': 'true',
                },
                timeout: isVideo ? 300000 : 120000,
            });

            if (res.data.success) {
                if (adId && res.data.data && Array.isArray(res.data.data[mediaType])) {
                    onUrlsChange && onUrlsChange(res.data.data[mediaType]);
                } else if (Array.isArray(res.data.data?.urls)) {
                    onUrlsChange && onUrlsChange([...urls, ...res.data.data.urls]);
                } else if (Array.isArray(res.data.added)) {
                    onUrlsChange && onUrlsChange([...urls, ...res.data.added]);
                }
            }
        } catch (err) {
            setError(err.response?.data?.error || err.message || 'Upload failed');
        } finally {
            setUploading(false);
        }
    };

    const handleRemoveUrl = async (url) => {
        if (disabled) return;
        setError('');

        try {
            const token = localStorage.getItem('token');
            if (adId) {
                await axios.delete(`${API_BASE}/api/ads/${adId}/media`, {
                    params: { mediaType, url },
                    headers: {
                        Authorization: `Bearer ${token}`,
                        'ngrok-skip-browser-warning': 'true',
                    },
                });
            } else {
                await axios.delete(`${API_BASE}/api/ads/media`, {
                    params: { url },
                    headers: {
                        Authorization: `Bearer ${token}`,
                        'ngrok-skip-browser-warning': 'true',
                    },
                });
            }
            onUrlsChange && onUrlsChange(urls.filter((u) => u !== url));
        } catch (err) {
            setError(err.response?.data?.error || err.message || 'Delete failed');
        }
    };

    const onDrop = (e) => {
        e.preventDefault();
        setDragOver(false);
        if (disabled) return;
        handleFiles(e.dataTransfer.files);
    };

    const onDragOver = (e) => {
        e.preventDefault();
        if (!disabled) setDragOver(true);
    };

    return (
        <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-700">{label}</label>
            <div
                onDrop={onDrop}
                onDragOver={onDragOver}
                onDragLeave={() => setDragOver(false)}
                className={`border-2 border-dashed rounded-lg p-4 text-center transition-colors ${
                    disabled
                        ? 'border-gray-200 bg-gray-50 opacity-60'
                        : dragOver
                            ? 'border-blue-500 bg-blue-50'
                            : 'border-gray-300 bg-gray-50 cursor-pointer hover:border-blue-400'
                }`}
                onClick={() => !disabled && inputRef.current && inputRef.current.click()}
            >
                <input
                    ref={inputRef}
                    type="file"
                    accept={accept}
                    multiple={multiple}
                    className="hidden"
                    disabled={disabled}
                    onChange={(e) => {
                        handleFiles(e.target.files);
                        e.target.value = '';
                    }}
                />
                <FiUpload className="mx-auto h-8 w-8 text-gray-400" />
                <p className="mt-1 text-sm text-gray-600">
                    Drag & drop or click to upload {isVideo ? 'videos' : isGif ? 'GIFs' : 'images'}
                </p>
            </div>
            {uploading && <p className="text-sm text-blue-600">Uploading…</p>}
            {error && <p className="text-sm text-red-600">{error}</p>}

            {urls.length > 0 && (
                <div className="flex flex-wrap gap-2 mt-2">
                    {urls.map((url) => {
                        const displaySrc = isVideo
                            ? getAdsVideoSrc(url, adId, Boolean(videoDirectFallback[url]))
                            : getAdsMediaDisplayUrl(url, mediaType, adId);
                        return (
                        <div key={url} className="relative group">
                            {isVideo ? (
                                <video
                                    src={displaySrc}
                                    className="w-24 h-24 object-cover rounded border border-gray-200 bg-black"
                                    autoPlay
                                    loop
                                    muted
                                    playsInline
                                    preload="auto"
                                    onError={() => {
                                        if (!videoDirectFallback[url]) {
                                            setVideoDirectFallback((prev) => ({ ...prev, [url]: true }));
                                        }
                                    }}
                                />
                            ) : (
                                <img
                                    src={displaySrc}
                                    alt=""
                                    className="w-24 h-24 object-cover rounded border border-gray-200"
                                />
                            )}
                            {!disabled && (
                                <button
                                    type="button"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        handleRemoveUrl(url);
                                    }}
                                    className="absolute -top-1 -right-1 p-0.5 bg-red-500 text-white rounded-full opacity-90 group-hover:opacity-100"
                                >
                                    <FiX className="w-4 h-4" />
                                </button>
                            )}
                        </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
};

export default AdsMediaUploadSection;
